import type { RoomRecordingSourceKind } from './CompanionVision';
import { isParticipantProgramMediaPath } from './ProgramParticipantMedia';

/**
 * Room Server-Side MP4 Recording Types
 *
 * Types for MediaMTX playback API integration and recording service.
 * Supports ephemeral 5-minute rolling buffer downloads.
 */

export const DEFAULT_RECORDING_STREAM_KEY = 'whip';

export const ROOM_RECORDING_SOURCE_KINDS = [
  'gameplay_share',
  'program_stage',
  'room_stage',
  'screen_share',
] as const satisfies readonly RoomRecordingSourceKind[];

const ROOM_RECORDING_STREAM_PREFIX_BY_SOURCE_KIND: Record<RoomRecordingSourceKind, string> = {
  gameplay_share: 'gameplay',
  program_stage: 'townhall',
  room_stage: 'room-stage',
  screen_share: 'screen-share',
};

/**
 * MediaMTX path name for a room recording source.
 *
 * The WHIP endpoint is `${path}/whip`; the playback API uses only `path`.
 */
export function getRoomRecordingStreamPath(
  roomId: string,
  sourceKind: RoomRecordingSourceKind = 'room_stage',
): string {
  const prefix = ROOM_RECORDING_STREAM_PREFIX_BY_SOURCE_KIND[sourceKind] ?? 'townhall';
  return `${prefix}/${roomId}`;
}

export function isRoomRecordingSourceKind(value: unknown): value is RoomRecordingSourceKind {
  return typeof value === 'string' && ROOM_RECORDING_SOURCE_KINDS.includes(value as RoomRecordingSourceKind);
}

export function isRoomRecordingStreamPath(value: string): boolean {
  if (isParticipantProgramMediaPath(value)) return true;
  const prefixes = new Set(Object.values(ROOM_RECORDING_STREAM_PREFIX_BY_SOURCE_KIND));
  const [prefix, roomId, ...rest] = value.split('/');
  if (!prefix || !roomId) return false;
  return (
    rest.length === 0 &&
    prefixes.has(prefix) &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(roomId)
  );
}

/**
 * Lower-case, runs of non-alphanumerics to one dash, dashes trimmed from the ends; `maxLength`
 * cuts the result. Used for generated filenames and the program room slug. ⚠ Not yet the only slug
 * rule in the monorepo — `sanitizeRoomName` (web-next) and the avatar/talk-show admin pages each
 * still have their own, and they do not agree on underscores or runs. Migrating those changes
 * stored slugs, so it is deliberately out of scope here.
 */
export function slugify(text: string, options: { maxLength?: number } = {}): string {
  const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return options.maxLength ? slug.slice(0, options.maxLength) : slug;
}

export function getRoomRecordingFilenamePrefix(
  roomId: string,
  sourceKind: RoomRecordingSourceKind = 'room_stage',
): string {
  return `${sourceKind.replace(/_/g, '-')}-${roomId}`;
}

/**
 * MediaMTX /list endpoint response
 * Represents a timespan of recorded segments
 */
export interface MediaMTXTimespan {
  /** RFC3339 timestamp of segment start */
  start: string;
  /** Duration in seconds */
  duration: number;
  /** Relative URL for downloading this segment */
  url: string;
}

/**
 * Result from MediaMTX /get endpoint stream
 */
export interface RecordingStreamResult {
  /** Readable stream of MP4 data */
  stream: ReadableStream | NodeJS.ReadableStream;
  /** Estimated content length in bytes (if available) */
  contentLength?: number;
  /** ISO 8601 start timestamp */
  startTime: string;
  /** Total duration in seconds */
  durationSeconds: number;
}

/**
 * Recording metadata for token generation
 */
/**
 * A finished GenTV episode — every shot that aired, joined into one file — offered to every
 * participant until the host closes the mode. It rides inside the download token, so the file
 * route needs no lookup and, the token being signed, the key cannot be swapped.
 */
export interface GentvEpisodeDownload {
  /** S3 key under gentv-episodes/<roomId>/ */
  key: string;
  episodeId: string;
  filename: string;
  bytes: number;
  seconds: number;
  /** ISO 8601, when the episode was cut */
  createdAt: string;
}

/** One take of a GenTV episode as its ledger records it: where it sat in the show, and its object. */
export interface GentvEpisodeTake {
  id: string;
  shotId: string;
  sceneId: string;
  /** `exit`, `rise` and `broll` are the scenario's pre-rendered environment (#3976): shared CDN objects, never per-episode uploads. */
  kind: 'core' | 'coda' | 'exit' | 'rise' | 'broll';
  version: number;
  seconds: number;
  /** S3 key of the normalised take, under gentv-episodes/<roomId>/<episodeId>/takes/ */
  key: string;
  /** When the take actually went on air — the episode's order across channels. */
  airedAt: number;
}

/** What may be offered and what the download route will stream. One number, both sides. */
export const GENTV_EPISODE_MAX_BYTES = 512 * 1024 * 1024;

/** What the download request accepts: a MediaMTX buffer source, or the finished GenTV episode. */
export type RecordingDownloadSourceKind = RoomRecordingSourceKind | 'gentv_episode';

export interface RecordingMetadata {
  roomId: string;
  streamKey?: string;
  sourceKind?: RecordingDownloadSourceKind;
  userId: string;
  /** Buffer downloads only; an episode's length is in `episode`. */
  durationMinutes?: number;
  /** Timestamp when recording was requested */
  requestedAt: Date;
  /** Set for a GenTV episode download; the MediaMTX fields above are then unused. */
  episode?: GentvEpisodeDownload;
}

/**
 * Download token payload (JWT)
 */
export interface RecordingDownloadTokenPayload {
  /** JWT ID for one-time use tracking */
  jti: string;
  /** Room ID */
  roomId: string;
  /** User ID who requested download */
  userId: string;
  /** Token type identifier */
  type: 'townhall_recording_download' | 'room_recording_download' | 'gentv_episode_download';
  /** Issued at (Unix timestamp) */
  iat: number;
  /** Expires at (Unix timestamp) */
  exp: number;
  /** Stream key */
  streamKey?: string;
  /** Room recording source kind, or `gentv_episode` */
  sourceKind?: RecordingDownloadSourceKind;
  /** Duration in minutes */
  durationMinutes?: number;
  /** A GenTV episode download; when present the token names the file and no buffer is consulted. */
  episode?: GentvEpisodeDownload;
}

/**
 * Recording availability status
 */
export interface RecordingAvailability {
  /** Whether recording buffer is available */
  available: boolean;
  /** Reason if not available */
  reason?: 'no_broadcast' | 'buffer_empty' | 'buffer_too_short' | 'mediamtx_error';
  /** Available duration in seconds */
  availableDurationSeconds?: number;
  /** Number of segments found */
  segmentCount?: number;
  /** Diagnostic detail for non-user-facing MediaMTX failures. */
  errorMessage?: string;
}

/** Recording download request (POST /api/room/recording/download) */
export interface RecordingDownloadRequest {
  /** Room ID (UUID format) */
  roomId: string;
  /** Shared recording source, or `gentv_episode`. Defaults to program_stage for legacy callers. */
  sourceKind?: RecordingDownloadSourceKind;
  /** GenTV only: explicit current or immediately prior episode, never an implicit film fallback. */
  episodeId?: string;
  /** Stream key (alphanumeric + hyphens/underscores). Defaults to "whip". */
  streamKey?: string;
  /** Duration in minutes (10 seconds - 5.0) */
  durationMinutes?: number;
}

/**
 * Recording download response
 */
export interface RecordingDownloadResponse {
  /** Signed download URL with embedded token */
  downloadUrl: string;
  /** Token expiration timestamp (ISO 8601) */
  expiresAt: string;
  /** Estimated file size in bytes */
  estimatedSizeBytes: number;
  /** Recording start timestamp (ISO 8601) */
  startTime: string;
  /** Recording duration in seconds */
  durationSeconds: number;
  /** Source captured by the download */
  sourceKind?: RecordingDownloadSourceKind;
}

/**
 * MediaMTX active-path status for a shared room recording source.
 */
export interface RecordingPathStatusResponse {
  roomId: string;
  sourceKind: RoomRecordingSourceKind;
  streamPath: string;
  /** Whether MediaMTX currently has an active path for this source. */
  active: boolean;
  /** Whether MediaMTX reports the path as ready for readers/playback. */
  ready: boolean;
  /** Number of MediaMTX paths currently active. */
  activePathCount: number;
  /** Number of active paths that belong to room recording prefixes. */
  activeRecordingPathCount: number;
  /** Autoscaling hint: media origin should remain online while any recording path is active. */
  shouldKeepMediaServerAlive: boolean;
  /** Timestamp when status was checked. */
  checkedAt: string;
  /** Whether the same MediaMTX recording buffer check used by download currently passes. */
  downloadReady?: boolean;
  /** Reason the recording buffer is not yet downloadable. */
  bufferReason?: RecordingAvailability['reason'];
  /** Available recording buffer duration in seconds, when known. */
  bufferAvailableDurationSeconds?: number;
  /** Available recording segment count, when known. */
  bufferSegmentCount?: number;
  /**
   * #2537 — health of the process that PRODUCES the recording, as opposed to the MediaMTX path
   * that stores it.
   *
   * Every other field here describes the storage side. When the publisher or compositor is down,
   * MediaMTX correctly reports no active path, and the panel reads that as "still buffering" —
   * indistinguishable from a recording that simply has not started yet. That is how #3041's total
   * recording loss presented to the host as an ordinary "MP4 pending" for an entire session.
   *
   * Populated by the web-next proxy, not by signaling: the task health lives in Nomad, which
   * signaling has no access to. Absent when the runtime cannot be determined, which callers must
   * treat as "unknown", never as "unavailable".
   */
  recorder?: {
    /** False only when the recorder is known to be unable to produce output. */
    available: boolean;
    /** Publisher task state as Nomad reports it. */
    state?: string | null;
    /**
     * Restarts within the current allocation. A positive count alongside `available: true` is a
     * crash loop — healthy at any instant, broken over time.
     */
    restarts?: number | null;
    /** Why the recorder is unavailable or unstable, when there is something to say. */
    message?: string | null;
  };
  details?: {
    name?: string;
    readerCount?: number;
    tracks?: string[];
    sourceType?: string;
    readyTime?: string;
    bufferErrorMessage?: string;
  };
}

/**
 * Error response from recording service
 */
export interface RecordingErrorResponse {
  error: string;
  message: string;
  statusCode: number;
  details?: Record<string, unknown>;
}
